import styled from '@emotion/styled'
import Delete from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Tooltip } from '@mui/material'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { CustomFieldType, SupportedCustomFieldConnectors } from './custom-field-type'
import { validateDisplayName, validateFieldName } from './custom-field-validator'
import { colors } from '../../global/constants/colors'
import { t } from '@lingui/core/macro'

const ListItemPadding = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
`

const FieldConfiguration = styled.div`
  display: flex;
  align-items: center;
  width: 500;
  justify-content: space-between;
`

const Spacer = styled.div`
  width: 15px;
`

const DeleteContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 50px;
  margin-bottom: 15px;
`

const ErrorTextContainer = styled.div`
  height: 15px;
`

const Field = styled.div`
  width: 240px;
`

const DeleteIcon = styled(Delete)`
  color: ${colors.gray.medium};
  cursor: pointer;
`

interface CustomFieldRowProps {
  customField: CustomFieldType
  index: number
  handleChange: (customField: CustomFieldType, index: number) => void
  handleDelete: (index: number) => void
  allCustomFields: CustomFieldType[]
  connectorType: SupportedCustomFieldConnectors
  isHidden?: boolean
  isMapped: boolean
}

export const CustomFieldRow = ({
  customField,
  index,
  handleChange,
  handleDelete,
  allCustomFields,
  connectorType,
  isHidden,
  isMapped,
}: CustomFieldRowProps) => {
  const { display_name, custom_field_name } = customField

  const remoteEntity = validateFieldName(connectorType, customField, allCustomFields)

  const fieldLabel = validateDisplayName(customField, allCustomFields)

  if (isHidden) {
    return <div />
  }

  return (
    <ListItemPadding>
      <FieldConfiguration>
        <Field>
          <TextField
            variant="outlined"
            label="Remote Field ID"
            fullWidth
            value={custom_field_name}
            onChange={e => {
              const field: CustomFieldType = {
                ...customField,
                custom_field_name: e.target.value,
              }
              handleChange(field, index)
            }}
            error={custom_field_name !== '' && !remoteEntity.isValid}
          />
          <ErrorTextContainer>
            {!remoteEntity.isValid && (
              <Typography variant="caption" color="error">
                {remoteEntity.errorMsg}
              </Typography>
            )}
          </ErrorTextContainer>
        </Field>
        <Spacer />
        <Field>
          <TextField
            variant="outlined"
            label={t`Field label`}
            fullWidth
            value={display_name}
            onChange={e => {
              const field: CustomFieldType = {
                ...customField,
                display_name: e.target.value,
              }
              handleChange(field, index)
            }}
            error={display_name !== '' && !fieldLabel.isValid}
          />
          <ErrorTextContainer>
            {!fieldLabel.isValid && (
              <Typography variant="caption" color="error">
                {fieldLabel.errorMsg}
              </Typography>
            )}
          </ErrorTextContainer>
        </Field>
      </FieldConfiguration>
      <DeleteContainer>
        {isMapped ? (
          <Tooltip
            title={t`This custom field is currently mapped on a template. Remove the field mapping before deleting.`}
          >
            <DeleteForeverIcon color="disabled" />
          </Tooltip>
        ) : (
          <DeleteIcon
            onClick={() => {
              handleDelete(index)
            }}
          />
        )}
      </DeleteContainer>
    </ListItemPadding>
  )
}
